/** Copiado de la app actual (src/pages/checkin/.../MiDniQrCapture/midniQrImage.ts) sin cambios. */
export interface Point {
  x: number
  y: number
}

export interface CropBounds {
  x: number
  y: number
  width: number
  height: number
}

export interface ScanRegion {
  x: number
  y: number
  width: number
  height: number
  downScaledWidth: number
  downScaledHeight: number
}

export const getContinuousScanRegion = (
  videoWidth: number,
  videoHeight: number,
  maxDimension = 1600
): ScanRegion => {
  if (videoWidth <= 0 || videoHeight <= 0) {
    return { x: 0, y: 0, width: 1, height: 1, downScaledWidth: 1, downScaledHeight: 1 }
  }

  const scale = Math.min(1, maxDimension / Math.max(videoWidth, videoHeight))
  return {
    x: 0,
    y: 0,
    width: videoWidth,
    height: videoHeight,
    downScaledWidth: Math.max(1, Math.round(videoWidth * scale)),
    downScaledHeight: Math.max(1, Math.round(videoHeight * scale)),
  }
}

export const getCenteredContinuousScanRegion = (
  videoWidth: number,
  videoHeight: number,
  maxDimension = 2400,
  coverage = 0.84
): ScanRegion => {
  if (videoWidth <= 0 || videoHeight <= 0) {
    return getContinuousScanRegion(videoWidth, videoHeight, maxDimension)
  }

  const width = Math.max(1, Math.round(videoWidth * coverage))
  const height = Math.max(1, Math.round(videoHeight * coverage))
  const x = Math.floor((videoWidth - width) / 2)
  const y = Math.floor((videoHeight - height) / 2)
  const scale = Math.min(1, maxDimension / Math.max(width, height))

  return {
    x,
    y,
    width,
    height,
    downScaledWidth: Math.max(1, Math.round(width * scale)),
    downScaledHeight: Math.max(1, Math.round(height * scale)),
  }
}

export const getQrCropBounds = (
  cornerPoints: Point[],
  imageWidth: number,
  imageHeight: number,
  detectionScale: number,
  marginRatio = 0.125
): CropBounds => {
  if (cornerPoints.length < 4 || imageWidth <= 0 || imageHeight <= 0 || detectionScale <= 0) {
    throw new Error('Invalid QR crop data')
  }

  const xs = cornerPoints.map(({ x }) => x / detectionScale)
  const ys = cornerPoints.map(({ y }) => y / detectionScale)
  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)
  const qrWidth = maxX - minX
  const qrHeight = maxY - minY

  if (qrWidth <= 0 || qrHeight <= 0) throw new Error('Invalid QR corner points')

  const margin = Math.max(qrWidth, qrHeight) * marginRatio
  const x = Math.max(0, Math.floor(minX - margin))
  const y = Math.max(0, Math.floor(minY - margin))
  const right = Math.min(imageWidth, Math.ceil(maxX + margin))
  const bottom = Math.min(imageHeight, Math.ceil(maxY + margin))

  return { x, y, width: right - x, height: bottom - y }
}

export const cropQrFromVideoFrame = (
  video: HTMLVideoElement,
  cornerPoints: Point[]
): string => {
  const { videoWidth, videoHeight } = video
  const bounds = getQrCropBounds(cornerPoints, videoWidth, videoHeight, 1)
  const frameCanvas = document.createElement('canvas')
  const outputCanvas = document.createElement('canvas')

  try {
    frameCanvas.width = videoWidth
    frameCanvas.height = videoHeight
    const frameContext = frameCanvas.getContext('2d', { alpha: false })
    if (!frameContext) throw new Error('Canvas is not available')
    frameContext.drawImage(video, 0, 0, videoWidth, videoHeight)

    const outputScale = Math.min(1, 1024 / Math.max(bounds.width, bounds.height))
    outputCanvas.width = Math.max(1, Math.round(bounds.width * outputScale))
    outputCanvas.height = Math.max(1, Math.round(bounds.height * outputScale))
    const outputContext = outputCanvas.getContext('2d', { alpha: false })
    if (!outputContext) throw new Error('Canvas is not available')
    outputContext.drawImage(
      frameCanvas,
      bounds.x,
      bounds.y,
      bounds.width,
      bounds.height,
      0,
      0,
      outputCanvas.width,
      outputCanvas.height
    )

    return outputCanvas.toDataURL('image/jpeg', 0.9)
  } finally {
    frameCanvas.width = 0
    frameCanvas.height = 0
    outputCanvas.width = 0
    outputCanvas.height = 0
  }
}
